import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Inject,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import _partition from 'lodash.partition';
import { forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  defaultIfEmpty,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  finalize,
  map,
  switchMap,
  tap
} from 'rxjs/operators';

import { ArrayHelper, FormHelper } from 'tiime-expert-utils';
import {
  DialogBase,
  FallbackTextDirective,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeIconPropertyDirective,
  TiimeLargePropertyDirective,
  TiimeMenuModule,
  TiimePropertiesDirective,
  TiimePropertyDirective,
  TiimeSnackbarModule,
  TiimeSnackbarService,
  TiimeTabsModule,
  TiimeTooltipModule
} from 'tiime-material';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { CompanyApiService } from '@api-services/company-api.service';
import { PersonApiService } from '@api-services/person-api.service';
import { SignatoryApiService } from '@api-services/signatory-api.service';
import { AntiMoneyLaundering } from '@common/models/anti-money-laundering';
import { Level } from '@enums/level.enum';
import { MissionAcceptanceFileType } from '@enums/mission-acceptance-file-type.enum';
import { MissionAcceptanceStatus } from '@enums/mission-acceptance-status.enum';
import { MissionAcceptanceType } from '@enums/mission-acceptance-type.enum';
import { UserType } from '@enums/user-type.enum';
import { MissionAcceptanceForm } from '@forms/mission-acceptance-form/mission-acceptance-form';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { Contract } from '@gi/models/contract';
import { FileHelper } from '@helpers/file.helper';
import { remainSubscribedOnError } from '@helpers/rxjs.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { ApeCode } from '@models/ape-code';
import { BusinessUser } from '@models/business-user';
import { CodeAndLabel } from '@models/code-and-label';
import { Company } from '@models/company';
import { MissionAcceptance } from '@models/mission-acceptance/mission-acceptance';
import { MissionAcceptanceFile } from '@models/mission-acceptance-file';
import { Signatory } from '@models/signatory';
import { TaxRegime } from '@models/tax-regime';
import { User } from '@models/user';
import { VatSystem } from '@models/vat-system';
import { ContractFacade } from '@modules/core/facades/contract.facade';
import { MissionAcceptanceFacade } from '@modules/core/facades/mission-acceptance.facade';
import {
  businessUserCanAccessToContracts,
  businessUserSelector
} from '@modules/core/store/business-user/business-user-selector';
import { AntiMoneyLaunderingFormComponent } from '@modules/shared/common/components/forms/anti-money-laundering-form/anti-money-laundering-form.component';
import { ExternalUrlService } from '@services/external-url.service';
import { AddressPropertyComponent } from '@shared-components/address-property/address-property.component';
import { CompanyPropertiesComponent } from '@shared-components/company-properties/company-properties.component';
import { RefreshButtonComponent } from '@shared-components/refresh-button/refresh-button.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogModule } from '@shared-dialogs/confirm-dialog/confirm-dialog.module';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { CreateUserDialogData } from '@shared-dialogs/create-user-dialog/create-user-dialog-data';
import { CreateUserDialogService } from '@shared-dialogs/create-user-dialog/create-user-dialog.service';
import { SelectContractDialogService } from '@shared-dialogs/select-contract-dialog/select-contract-dialog.service';
import { PhonePipe } from '@shared-pipes/phone/phone.pipe';
import { MissionAcceptanceStatusSelectComponent } from '@shared-selectors/mission-acceptance-status-select/mission-acceptance-status-select.component';
import { SignatorySelectComponent } from '@shared-selectors/signatory-select/signatory-select.component';
import { UserSelectComponent } from '@shared-selectors/user-select/user-select.component';

import { AcceptationFormComponent } from './acceptation-form/acceptation-form.component';
import { CreateOrUpdateMissionAcceptanceDialogData } from './create-or-update-mission-acceptance-dialog-data';
import {
  InformationsFormComponent,
  MissionAcceptanceFileSelectedOutput
} from './informations-form/informations-form.component';
import { MissionAcceptanceFormStatusIconComponent } from './mission-acceptance-form-status-icon/mission-acceptance-form-status-icon.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-or-update-mission-acceptance-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeDialogModule,
    TiimeButtonModule,
    TiimeTabsModule,
    TiimeIconModule,
    TiimeTooltipModule,
    TiimePropertiesDirective,
    TiimePropertyDirective,
    TiimeLargePropertyDirective,
    TiimeIconPropertyDirective,
    TiimeSnackbarModule,
    MissionAcceptanceFormStatusIconComponent,
    MissionAcceptanceStatusSelectComponent,
    InformationsFormComponent,
    AcceptationFormComponent,
    AntiMoneyLaunderingFormComponent,
    CompanyPropertiesComponent,
    AddressPropertyComponent,
    UserSelectComponent,
    RefreshButtonComponent,
    FallbackTextDirective,
    PhonePipe,
    SignatorySelectComponent,
    ConfirmDialogModule,
    TiimeMenuModule
  ],
  templateUrl: './create-or-update-mission-acceptance-dialog.component.html',
  styleUrls: ['./create-or-update-mission-acceptance-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrUpdateMissionAcceptanceDialogComponent
  extends DialogBase<CreateOrUpdateMissionAcceptanceDialogComponent>
  implements OnInit
{
  readonly missionAcceptanceForm: MissionAcceptanceForm = new MissionAcceptanceForm();
  readonly statuses$: Observable<CodeAndLabel<MissionAcceptanceStatus>[]> =
    this.commonDataApiService.getMissionAcceptanceStatuses();
  readonly riskLevels$: Observable<CodeAndLabel<Level>[]> = this.commonDataApiService.getMissionAcceptanceRiskLevels();
  readonly types$: Observable<CodeAndLabel<MissionAcceptanceType>[]> =
    this.commonDataApiService.getMissionAcceptanceTypes();
  readonly taxRegimes$: Observable<TaxRegime[]> = this.commonDataApiService.getTaxRegimes();
  readonly vatSystems$: Observable<VatSystem[]> = this.commonDataApiService.getVatSystems();
  readonly fileTypes$: Observable<CodeAndLabel<MissionAcceptanceFileType>[]> =
    this.commonDataApiService.getMissionAcceptanceFileTypes();
  readonly companyDisplayedProperties = ['name', 'legalForm', 'apeCode', 'taxRegime', 'vatSystem'];
  readonly company: Signal<Company> = toSignal(
    this.missionAcceptanceForm.valueChanges.pipe(
      debounceTime(200),
      map(() => this.companyFieldsToCompany(this.missionAcceptanceForm.toMissionAcceptance()))
    )
  );
  readonly missionAcceptanceFormStatusValue: Signal<MissionAcceptanceStatus | null> = toSignal(
    this.missionAcceptanceForm.controls.missionAcceptanceStatus.valueChanges,
    { initialValue: null }
  );
  readonly missionAcceptanceFormSignatoryValue: Signal<Signatory | null> = toSignal(
    this.missionAcceptanceForm.controls.signatory.valueChanges,
    {
      initialValue: null
    }
  );
  readonly currentBusinessUser: Signal<BusinessUser> = toSignal(this.store.pipe(select(businessUserSelector)));
  readonly canAddContractLdmFromContract: Signal<boolean> = this.store.selectSignal(businessUserCanAccessToContracts);

  readonly users: WritableSignal<User[]> = signal<User[]>([]);
  readonly signatories: WritableSignal<Signatory[]> = signal<Signatory[]>([]);
  readonly MissionAcceptanceStatus = MissionAcceptanceStatus;

  readonly canDownloadMissionAcceptancePdf: Signal<boolean> = computed(
    () => this.missionAcceptanceFormStatusValue() === MissionAcceptanceStatus.validated
  );
  readonly downloadMissionAcceptancePdfTooltip: Signal<string> = computed(() =>
    this.canDownloadMissionAcceptancePdf()
      ? 'Télécharger le rapport'
      : `Le rapport n'est disponible qu'une fois l'acceptation validée`
  );
  readonly canValidateMission: Signal<boolean> = computed(() => {
    const isToValidate = this.missionAcceptanceFormStatusValue() === MissionAcceptanceStatus.toValidate;
    const currentBusinessUserIsSignatory =
      this.missionAcceptanceFormSignatoryValue()?.user?.id === this.currentBusinessUser()?.id;

    return isToValidate && currentBusinessUserIsSignatory;
  });
  readonly canUnlockMission: Signal<boolean> = computed(() => {
    const isValidated = this.missionAcceptanceFormStatusValue() === MissionAcceptanceStatus.validated;
    const signatoriesIncludesCurrentBusinessUser = this.signatories().some(
      (signatory: Signatory) => signatory.user?.id === this.currentBusinessUser()?.id
    );

    return isValidated && signatoriesIncludesCurrentBusinessUser;
  });

  private shouldForceCloseWithMissionAcceptance: boolean;

  private readonly missionAcceptanceFacade: MissionAcceptanceFacade = inject(MissionAcceptanceFacade);
  private readonly subscription = new Subscription();

  constructor(
    readonly dialogRef: MatDialogRef<CreateOrUpdateMissionAcceptanceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: CreateOrUpdateMissionAcceptanceDialogData,
    private readonly commonDataApiService: CommonDataApiService,
    private readonly personApiService: PersonApiService,
    private readonly createUserDialogService: CreateUserDialogService,
    private readonly companyApiService: CompanyApiService,
    private readonly signatoryApiService: SignatoryApiService,
    private readonly store: Store<AppStoreState>,
    private readonly snackbar: TiimeSnackbarService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly externalUrlService: ExternalUrlService,
    private readonly selectContractDialogService: SelectContractDialogService,
    private readonly contractFacade: ContractFacade
  ) {
    super(dialogRef);

    this.missionAcceptanceForm.fromMissionAcceptance(data.missionAcceptance);
    this.manageFormDisabled();
  }

  ngOnInit(): void {
    this.observeContactChange();
    this.observeAntimoneyLanderingChange();
    this.initSignatories();
    this.initUsers();
  }

  override cancel(): void {
    if (this.shouldForceCloseWithMissionAcceptance && this.missionAcceptanceForm.controls.id.value) {
      this.missionAcceptanceFacade
        .getMissionAcceptance(
          this.missionAcceptanceForm.controls.id.value,
          this.missionAcceptanceForm.controls.company.value.id
        )
        .pipe(tap((missionAcceptance: MissionAcceptance) => super.close(missionAcceptance)))
        .subscribe();
    } else {
      super.cancel();
    }
  }

  submitMissionAcceptance(): void {
    this.missionAcceptanceForm.removeToValidateControlsRequired();

    if (this.missionAcceptanceForm.invalid) {
      this.missionAcceptanceForm.markAllAsTouched();
      this.displayInvalidFormSnackbar();
      return;
    }

    this.isLoading.set(true);
    this.missionAcceptanceForm.disable(FormHelper.shouldNotEmitEvent);

    this.saveMissionAcceptance()
      .pipe(
        tap((missionAcceptance: MissionAcceptance) => this.dialogRef.close(missionAcceptance)),
        finalize(() => {
          this.isLoading.set(false);
          this.manageFormDisabled();
        })
      )
      .subscribe();
  }

  validate(): void {
    this.openConfirmChangeMissionAcceptanceStatusDialog(
      `Valider l'acceptation de mission`,
      `En validant cette acceptation de mission, toutes les informations actuelles, y compris les documents joints, seront définitivement figées et ne pourront plus être modifiées.`
    )
      .pipe(tap(() => this.changeMissionAcceptanceStatus(MissionAcceptanceStatus.validated)))
      .subscribe();
  }

  refuse(): void {
    this.openConfirmChangeMissionAcceptanceStatusDialog(
      `Refuser l'acceptation de mission`,
      `En refusant cette acceptation de mission, elle passera au statut 'En cours' et sera à nouveau modifiable.`
    )
      .pipe(tap(() => this.changeMissionAcceptanceStatus(MissionAcceptanceStatus.inProgress)))
      .subscribe();
  }

  unlock(): void {
    this.openConfirmUnlockMissionAcceptanceStatusDialog()
      .pipe(
        switchMap(() =>
          this.missionAcceptanceFacade.unlockMissionAcceptance(
            this.missionAcceptanceForm.controls.id.value,
            this.missionAcceptanceForm.controls.company.value?.id
          )
        ),
        tap((missionAcceptance: MissionAcceptance) => {
          this.missionAcceptanceForm.controls.missionAcceptanceStatus.setValue(missionAcceptance.status);
          this.manageFormDisabled();
          this.shouldForceCloseWithMissionAcceptance = true;
          this.snackbar.openSuccess(`La mission a bien été déverrouillée`);
        })
      )
      .subscribe();
  }

  changeMissionAcceptanceStatus(nextStatus: MissionAcceptanceStatus): void {
    const currentStatus = this.missionAcceptanceForm.controls.missionAcceptanceStatus.value;
    if (currentStatus === nextStatus) {
      return;
    }

    this.missionAcceptanceForm.removeToValidateControlsRequired();

    if (nextStatus === MissionAcceptanceStatus.toValidate) {
      this.missionAcceptanceForm.addToValidateControlsRequired();
    }

    if (this.missionAcceptanceForm.invalid) {
      this.missionAcceptanceForm.markAllAsTouched();
      this.displayInvalidFormSnackbar();
      return;
    }

    this.isLoading.set(true);
    this.missionAcceptanceForm.controls.missionAcceptanceStatus.setValue(nextStatus);
    this.missionAcceptanceForm.disable();

    const obs = [MissionAcceptanceStatus.todo, MissionAcceptanceStatus.inProgress].includes(currentStatus)
      ? this.saveMissionAcceptance()
      : of(this.missionAcceptanceForm.toMissionAcceptance());

    obs
      .pipe(
        tap((missionAcceptance: MissionAcceptance) => {
          this.missionAcceptanceForm.fromMissionAcceptance({ ...missionAcceptance, status: nextStatus });
          this.shouldForceCloseWithMissionAcceptance = true;
        }),
        switchMap((missionAcceptance: MissionAcceptance) =>
          this.missionAcceptanceFacade.updateMissionAcceptanceStatus(
            missionAcceptance.id,
            nextStatus,
            missionAcceptance.company?.id
          )
        ),
        tap((missionAcceptance: MissionAcceptance) => {
          this.isLoading.set(false);
          this.missionAcceptanceForm.fromMissionAcceptance(missionAcceptance);
          this.shouldForceCloseWithMissionAcceptance = true;
          this.manageFormDisabled();
        }),
        catchError(err => {
          this.isLoading.set(false);
          this.missionAcceptanceForm.controls.missionAcceptanceStatus.setValue(currentStatus);
          this.manageFormDisabled();

          return throwError(() => err);
        })
      )
      .subscribe();
  }

  refreshContactFields(): void {
    this.isLoading.set(true);

    this.subscription.add(
      this.getUsers(this.missionAcceptanceForm.controls.company.value.id)
        .pipe(
          tap((users: User[]) => {
            this.users.set(users);

            const contact: User = users.find(
              (user: User) => user.id === this.missionAcceptanceForm.controls.contact.value?.id
            );

            this.missionAcceptanceForm.controls.contact.setValue(contact);
          }),
          finalize(() => this.isLoading.set(false))
        )
        .subscribe()
    );
  }

  refreshCompanyFields(): void {
    this.isLoading.set(true);

    this.subscription.add(
      this.companyApiService
        .getCompany(this.missionAcceptanceForm.controls.company.value.id)
        .pipe(
          tap((company: Company) => this.missionAcceptanceForm.setCompanyFields(company)),
          finalize(() => this.isLoading.set(false))
        )
        .subscribe()
    );
  }

  openCreateUserDialog(): void {
    const createUserDialogData: CreateUserDialogData = {
      excludedAutocompleteUsers: this.users(),
      companyId: this.missionAcceptanceForm.controls.company.value?.id
    };

    this.createUserDialogService
      .open({
        data: createUserDialogData
      })
      .afterClosed()
      .pipe(
        filter((createdUser: User) => !!createdUser),
        tap((createdUser: User) => {
          this.missionAcceptanceForm.controls.contact.setValue(createdUser);
          this.users.update((users: User[]) => ArrayHelper.addOne(users, createdUser));
        })
      )
      .subscribe();
  }

  downloadMissionAcceptancePdf(): void {
    this.isLoading.set(true);
    this.missionAcceptanceFacade
      .downloadMissionAcceptancePdf(
        this.missionAcceptanceForm.controls.id.value,
        this.missionAcceptanceForm.controls.company.value.id
      )
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  removeFile(uniqId: string): void {
    this.missionAcceptanceForm.removeFile(uniqId);
  }

  downloadFile(fileId: number): void {
    this.missionAcceptanceFacade
      .downloadFile(
        this.missionAcceptanceForm.controls.id.value,
        fileId,
        this.missionAcceptanceForm.controls.company?.value?.id
      )
      .subscribe();
  }

  openFileInNewTab(fileId: number): void {
    this.missionAcceptanceFacade
      .openFileInNewTab(
        this.missionAcceptanceForm.controls.id.value,
        fileId,
        this.missionAcceptanceForm.controls.company?.value?.id
      )
      .subscribe();
  }

  onFileSelected(event: MissionAcceptanceFileSelectedOutput): void {
    const files = Array.from(event.fileList);
    const partition = _partition(files, (file: File) => FileHelper.isValid(file));
    const acceptedFiles: File[] = partition[0];
    const invalidFiles: File[] = partition[1];

    if (invalidFiles?.length) {
      this.snackbar.openError(FileHelper.getInvalidFileErrorMessage());
    }

    Array.from(acceptedFiles).forEach((file: File) => this.missionAcceptanceForm.addFileToUpload(file, event.type));
  }

  openAddContractFileDialog(): void {
    this.getCompanySignedContracts()
      .pipe(
        map((contracts: Contract[]) => contracts.filter(contract => contract.status === ContractStatus.signed)),
        switchMap((contracts: Contract[]) =>
          this.selectContractDialogService
            .open({
              data: {
                contracts,
                title: 'Intégrer une lettre de mission signée',
                alert: `Seuls les contrats signés dans le dossier sont affichés ici. Après avoir sélectionné un contrat et enregistré l’acceptation, l’intégration regroupera le contrat et les documents validés par le client en un seul document. À l'issue de cette opération, qui peut prendre quelques minutes, le document sera disponible en téléchargement.`
              }
            })
            .afterClosed()
        ),
        filter((contract: Contract) => !!contract),
        tap((contract: Contract) => this.missionAcceptanceForm.addContractFileToUpload(contract))
      )
      .subscribe();
  }

  openDirectoryLinkInNewTab(): void {
    this.externalUrlService.openDirectoryLinkWithUser(
      this.missionAcceptanceForm.controls.contactFirstName.value,
      this.missionAcceptanceForm.controls.contactLastName.value
    );
  }

  openGoogleLinkInNewTab(): void {
    this.externalUrlService.openGoogleLinkWithUser(
      this.missionAcceptanceForm.controls.contactFirstName.value,
      this.missionAcceptanceForm.controls.contactLastName.value
    );
  }

  openPappersLinkInNewTab(): void {
    this.externalUrlService.openPappersLinkWithUser(
      this.missionAcceptanceForm.controls.contactFirstName.value,
      this.missionAcceptanceForm.controls.contactLastName.value
    );
  }

  private getCompanySignedContracts(): Observable<Contract[]> {
    this.isLoading.set(true);

    return this.contractFacade
      .getCompanyContracts(this.missionAcceptanceForm.controls.company.value.id)
      .pipe(finalize(() => this.isLoading.set(false)));
  }

  private saveMissionAcceptance(): Observable<MissionAcceptance> {
    const missionAcceptance: MissionAcceptance = this.missionAcceptanceForm.toMissionAcceptance();
    const missionAcceptanceFilesToUpload: MissionAcceptanceFile[] = this.missionAcceptanceForm.getFilesToUpload();
    const missionAcceptanceFilesToDeleteIds: number[] = this.missionAcceptanceForm.getFilesToDelete();
    const missionAcceptanceContractFilesToUpload: MissionAcceptanceFile[] =
      this.missionAcceptanceForm.getContractsFilesToUpload();

    return this.missionAcceptanceFacade
      .getAntiMoneyLaunderingConclusions(missionAcceptance.antiMoneyLaundering, missionAcceptance.company?.id)
      .pipe(
        map((antiMoneyLaundering: AntiMoneyLaundering) => ({
          ...missionAcceptance,
          antiMoneyLaundering: { ...missionAcceptance.antiMoneyLaundering, antiMoneyLaundering }
        })),
        switchMap((missionAcceptanceWithConclusions: MissionAcceptance) =>
          missionAcceptanceWithConclusions.id
            ? this.missionAcceptanceFacade.updateMissionAcceptance(
                missionAcceptanceWithConclusions,
                missionAcceptance.company?.id
              )
            : this.missionAcceptanceFacade.createMissionAcceptance(missionAcceptanceWithConclusions)
        ),
        switchMap((missionAcceptance: MissionAcceptance) => {
          const companyId: number = missionAcceptance.company?.id;

          return forkJoin(
            [of(missionAcceptance)].concat(
              missionAcceptanceFilesToDeleteIds
                .map((fileToDeleteId: number) =>
                  this.missionAcceptanceFacade
                    .deleteMissionAcceptanceFile(missionAcceptance.id, fileToDeleteId, companyId)
                    .pipe(
                      map(() => fileToDeleteId),
                      catchError(err => of(err))
                    )
                )
                .concat(
                  missionAcceptanceFilesToUpload
                    .map((fileToUpload: MissionAcceptanceFile) =>
                      this.missionAcceptanceFacade
                        .addMissionAcceptanceFile(missionAcceptance, fileToUpload, companyId)
                        .pipe(catchError(err => of(err)))
                    )
                    .concat(
                      missionAcceptanceContractFilesToUpload.length
                        ? [
                            this.missionAcceptanceFacade
                              .addContractsMissionAcceptanceFiles(
                                missionAcceptance,
                                missionAcceptanceContractFilesToUpload,
                                missionAcceptance.company?.id
                              )
                              .pipe(catchError(err => of(err)))
                          ]
                        : []
                    )
                )
            )
          );
        }),
        defaultIfEmpty([]),
        map((datas: (MissionAcceptance | number | MissionAcceptanceFile | MissionAcceptanceFile[])[]) => {
          const savedMissionAcceptance: MissionAcceptance = datas.find(
            data => data instanceof MissionAcceptance
          ) as MissionAcceptance;
          const deletedFiles: number[] = datas.filter(data => typeof data === 'number') as number[];
          const uploadedFiles = (datas.find(data => Array.isArray(data)) as MissionAcceptanceFile[]) ?? [];
          datas.forEach(data => {
            if (data instanceof MissionAcceptanceFile) {
              uploadedFiles.push(data);
            }
          });

          deletedFiles.forEach((fileId: number) => {
            const index: number = savedMissionAcceptance.missionAcceptanceFiles.findIndex(
              (file: MissionAcceptanceFile) => file.id === fileId
            );
            if (index !== -1) {
              savedMissionAcceptance.missionAcceptanceFiles.splice(index, 1);
            }
          });

          uploadedFiles.forEach((file: MissionAcceptanceFile) =>
            savedMissionAcceptance.missionAcceptanceFiles.push(file)
          );

          return savedMissionAcceptance;
        }),
        tap(() => this.snackbar.openSuccess(`La mission a bien été ${missionAcceptance.id ? 'modifiée' : 'ajoutée'}`))
      );
  }

  private observeContactChange(): void {
    this.subscription.add(
      this.missionAcceptanceForm.controls.contact.valueChanges
        .pipe(
          distinctUntilKeyChanged('id'),
          tap((contact: User) => this.missionAcceptanceForm.setContactFields(contact))
        )
        .subscribe()
    );
  }

  private observeAntimoneyLanderingChange(): void {
    this.subscription.add(
      this.missionAcceptanceForm.controls.antiMoneyLaundering.valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          filter(() => this.missionAcceptanceForm.controls.antiMoneyLaundering.enabled),
          tap(() => this.isLoading.set(true)),
          switchMap((antiMoneyLaundering: AntiMoneyLaundering) =>
            this.missionAcceptanceFacade.getAntiMoneyLaunderingConclusions(
              antiMoneyLaundering,
              this.missionAcceptanceForm.controls.company.value.id
            )
          ),
          tap((antiMoneyLaundering: AntiMoneyLaundering) => {
            this.missionAcceptanceForm.controls.antiMoneyLaundering.setConclusions(antiMoneyLaundering);
            this.isLoading.set(false);
          }),
          catchError(error => {
            this.isLoading.set(false);
            return throwError(() => error);
          }),
          remainSubscribedOnError()
        )
        .subscribe()
    );
  }

  private companyFieldsToCompany(missionAcceptance: MissionAcceptance): Company {
    const company = new Company();
    company.name = missionAcceptance.companyName;
    company.legalForm = missionAcceptance.companyLegalForm;
    company.apeCode = missionAcceptance.companyApeCode ? new ApeCode(null, missionAcceptance.companyApeCode) : null;
    company.taxRegime = missionAcceptance.companyTaxRegime;
    company.vatSystem = missionAcceptance.companyVatSystem
      ? new VatSystem(null, missionAcceptance.companyVatSystem)
      : null;
    return company;
  }

  private manageFormDisabled(): void {
    const enableCondition = [MissionAcceptanceStatus.todo, MissionAcceptanceStatus.inProgress].includes(
      this.missionAcceptanceForm.controls.missionAcceptanceStatus.value
    );

    if (enableCondition) {
      this.missionAcceptanceForm.enable(FormHelper.shouldNotEmitEvent);
      return;
    }

    this.missionAcceptanceForm.disable(FormHelper.shouldNotEmitEvent);
  }

  private initUsers(): void {
    this.getUsers(this.missionAcceptanceForm.controls.company.value.id)
      .pipe(tap((users: User[]) => this.users.set(users)))
      .subscribe();
  }

  private initSignatories(): void {
    this.getSignatories(this.missionAcceptanceForm.controls.legalStructure.value.id)
      .pipe(tap((signatories: Signatory[]) => this.signatories.set(signatories)))
      .subscribe();
  }

  private getUsers(companyId: number): Observable<User[]> {
    return this.personApiService
      .getPersons(null, null, null, companyId)
      .pipe(map((users: User[]) => users.filter((user: User) => user.type === UserType.user)));
  }

  private getSignatories(legalStructureId: number): Observable<Signatory[]> {
    return this.signatoryApiService.getLegalStructureSignatories(legalStructureId);
  }

  private displayInvalidFormSnackbar(): void {
    this.snackbar.openError(`Certains champs requis n'ont pas été renseignés`);
  }

  private openConfirmChangeMissionAcceptanceStatusDialog(title: string, messages: string): Observable<boolean> {
    return this.confirmDialogService
      .open({
        data: new ConfirmDialogData(title, messages, 'Confirmer', 'Annuler')
      })
      .afterClosed()
      .pipe(filter((confirm: boolean) => confirm));
  }

  private openConfirmUnlockMissionAcceptanceStatusDialog(): Observable<boolean> {
    return this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          `Déverrouiller l'acceptation de mission`,
          'Cette action va déverrouiller l’acceptation de mission et la remettre en statut "En cours"',
          'Confirmer',
          'Annuler',
          '\nToutes les informations seront de nouveau modifiables.\n\nUne nouvelle validation sera nécessaire pour finaliser l’acceptation et obtenir le rapport.'
        )
      })
      .afterClosed()
      .pipe(filter((confirm: boolean) => confirm));
  }
}
